import Backbone from "backbone";
import * as preact from "preact";
import * as _ from "underscore";

import * as languageMap from "shared/languageMap";

import type UserModel from "models/UserModel";

interface Props {
  user: UserModel;
}

interface State {
  exampleWord: string;
  customUrl: string;
  previewUrl: string;
  showSaveButton: boolean;
}

class EditDictionaryView extends preact.Component<Props, State> {
  eventListener = _.extend(Backbone.Events);

  constructor(props: Props) {
    super(props);

    this.state = {
      exampleWord:
        languageMap.map[props.user.get("currentLearningLanguage")].name,
      customUrl:
        this.props.user.get("customDictionaries")[
          `${this.props.user.get("currentLearningLanguage")}${
            this.props.user.get("readerTranslations") === "off" ? "-mono" : ""
          }`
        ],
      previewUrl: "",
      showSaveButton: false,
    };

    if (this.state.customUrl === undefined) {
      this.setState({
        customUrl: this.defaultUrl(),
      });
    }

    this.previewDictionary(false);

    // Listen for changes to the user model
    this.eventListener.listenTo(this.props.user, "change", () =>
      this.forceUpdate()
    );
  }

  languageString() {
    return languageMap.map[this.props.user.get("currentLearningLanguage")]
      .english;
  }

  firstLanguageString() {
    return languageMap.map[this.props.user.get("firstLanguage")].english;
  }

  componentWillUnmount() {
    this.eventListener.stopListening();
  }

  calcPreviewUrl = (): string =>
    (this.state.customUrl ?? this.defaultUrl()).replace(
      "{{query}}",
      this.state.exampleWord
    );

  previewDictionary = (showSaveButton = true) => {
    this.setState({
      previewUrl: this.calcPreviewUrl(),
      showSaveButton,
    });
  };

  currentDictionaryCode = () =>
    `${this.props.user.get("currentLearningLanguage")}${
      this.props.user.get("readerTranslations") === "off" ? "-mono" : ""
    }`;

  handleRadioChange = async (event) => {
    const readerTranslations = event.currentTarget.value;

    this.props.user.save({
      readerTranslations,
    });

    await this.setState({
      customUrl:
        this.props.user.get("customDictionaries")[
          `${this.props.user.get("currentLearningLanguage")}${
            readerTranslations === "off" ? "-mono" : ""
          }`
        ] ?? this.defaultUrl(),
    });

    this.previewDictionary(false);
  };

  saveDictionary = () => {
    const customDictionaries = this.props.user.get("customDictionaries");

    if (this.state.customUrl === this.defaultUrl()) {
      // If the user has not changed the URL, do not save it
      delete customDictionaries[this.currentDictionaryCode()];
    } else {
      customDictionaries[this.currentDictionaryCode()] = this.state.customUrl;
    }

    this.props.user.save(
      {
        customDictionaries: customDictionaries,
      },
      { patch: true }
    );

    this.setState({ showSaveButton: false });
  };

  resetDictionary = async () => {
    const customDictionaries = this.props.user.get("customDictionaries");
    delete customDictionaries[this.currentDictionaryCode()];

    if (_.keys(customDictionaries).length === 0) {
      customDictionaries["dummy"] = true;
    }

    this.props.user.save(
      {
        customDictionaries: customDictionaries,
      },
      { patch: true }
    );

    await this.setState({ customUrl: this.defaultUrl() });
    this.previewDictionary();
  };

  defaultUrl = () => {
    if (this.props.user.get("readerTranslations") === "off") {
      // Monolingual mode
      return (
        languageMap.getEmbeddedDictionaryTemplate(
          this.props.user.get("currentLearningLanguage"),
          this.props.user.get("currentLearningLanguage")
        ).urlTemplate ??
        "http://mini.wordreference.com/mini/index.aspx?dict=esen&w={{query}}"
      );
    }
    // Translation mode
    return (
      languageMap.getEmbeddedDictionaryTemplate(
        this.props.user.get("currentLearningLanguage"),
        this.props.user.get("firstLanguage")
      ).urlTemplate ??
      "http://mini.wordreference.com/mini/index.aspx?dict=esen&w={{query}}"
    );
  };

  render() {
    const { exampleWord, customUrl } = this.state;

    const defaultUrl = this.defaultUrl();

    const readerTranslations = this.props.user.get("readerTranslations");
    const languageString = this.languageString();
    const firstLanguageString = this.firstLanguageString();

    return (
      <div>
        <table class="preferencesTable">
          <tbody>
            <tr>
              <td>External Dictionary</td>
              <td style={{ paddingBottom: "10px" }}>
                <div class="hangingIndent">
                  <label>
                    <input
                      type="radio"
                      name="readerTranslations"
                      value="on"
                      checked={readerTranslations !== "off"}
                      onChange={this.handleRadioChange}
                    />
                    Translation{" "}
                    <small>
                      (default) - a translation appears above each clicked word,
                      and the dictionary shows additional translations into{" "}
                      {firstLanguageString}
                      {firstLanguageString !== "English" && " or English"}
                    </small>
                  </label>
                </div>
                <div class="hangingIndent">
                  <label>
                    <input
                      type="radio"
                      name="readerTranslations"
                      value="off"
                      checked={readerTranslations === "off"}
                      onChange={this.handleRadioChange}
                    />
                    Monolingual{" "}
                    <small>
                      (advanced) - to stay in the language and only read{" "}
                      {languageString} definitions
                    </small>
                  </label>
                </div>
              </td>
            </tr>
            <tr>
              <td>Example {languageString} word</td>
              <td>
                <input
                  type="text"
                  placeholder="Enter word"
                  value={exampleWord}
                  onInput={(event) => {
                    this.setState({ exampleWord: event.currentTarget.value });
                  }}
                />
              </td>
            </tr>
            <tr>
              <td>
                URL{" "}
                <small>
                  with {"{{query}}"} where the word to translate should go
                </small>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Enter URL template"
                  value={customUrl}
                  onInput={(event) => {
                    this.setState({ customUrl: event.currentTarget.value });
                  }}
                />
                {!defaultUrl ? (
                  <small>
                    <i class="icon-arrow-up"></i> Replace this with a suitable{" "}
                    {{ languageString }} dictionary URL.
                  </small>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ margin: "10px 0" }}>
          {this.calcPreviewUrl() !== this.state.previewUrl ? (
            <button class="prominent" onClick={() => this.previewDictionary()}>
              <i class="icon-eye-open icon-large"></i> Preview changes
            </button>
          ) : null}{" "}
          {this.state.showSaveButton ? (
            <button class="prominent" onClick={this.saveDictionary}>
              <i class="icon-thumbs-up icon-large"></i> Save this dictionary
            </button>
          ) : null}{" "}
          {this.state.showSaveButton ? (
            <button
              class="prominent"
              onClick={() => {
                window.open(
                  this.state.previewUrl,
                  "_blank",
                  `location=no,menubar=no,toolbar=no,scrollbars=yes,width=400,height=550`
                );
              }}
              title="Use this button if the preview below is blocked by your browser."
            >
              Open in new window
            </button>
          ) : null}{" "}
          {customUrl !== defaultUrl ? (
            <button class="prominent" onClick={this.resetDictionary}>
              <i class="icon-ban-circle icon-large"></i> Reset to default
            </button>
          ) : null}
        </div>

        <iframe
          key={this.state.previewUrl}
          class="exampleIFrame"
          sandbox={`allow-forms allow-same-origin ${
            // Wordreference tries to auto-scroll parent webpage so don't allow scripts
            /wordreference\.com/.test(this.state.previewUrl)
              ? ""
              : "allow-scripts"
          }`}
          style={{ width: "100%", height: "400px", resize: "both" }}
          src={this.state.previewUrl}
        ></iframe>
        <p style={{ marginTop: "2px" }}>
          If the dictionary has failed to load (in Chrome you might see a sad
          face logo), click the "Open in new window" button above.
        </p>
      </div>
    );
  }
}

export default EditDictionaryView;
